import { supabase } from '../lib/supabase';

// ── Creditsaldo (weekmodel, fase 3 — 2026-08-09) ────────────────────────────
//
// DE leesroute. Het saldo staat in `credit_batches`, niet in een kolom: de
// poolkolommen (current_pt_credits en broertjes) zijn sinds fase 2 dood én
// bevatten stale residu, dus lezen levert een verkeerd getal op. Behandel elke
// nieuwe leesactie op zo'n kolom als een bug. Zelfde opzet als
// `src/services/database.js` in de app (aparte tech-stack, geen gedeeld
// package).

// Één RPC-call per cliënt: per (abonnement, pool) het saldo én de
// eerstvolgende vervalpost.
export async function getClientCreditOverview(clientId) {
  if (!clientId) return [];
  const { data, error } = await supabase.rpc('get_client_credit_overview', {
    p_client_id: clientId,
  });
  if (error) throw error;
  return data || [];
}

// Saldo's voor een LIJST van abonnementen (coachoverzichten over veel
// cliënten): één query op de view i.p.v. N RPC-calls. De view is
// security_invoker, dus de RLS van credit_batches bepaalt wat de coach ziet.
export async function getCreditBalancesForMemberships(membershipIds) {
  const ids = (membershipIds || []).filter(Boolean);
  if (ids.length === 0) return {};
  const { data, error } = await supabase
    .from('v_membership_credit_balance')
    .select('client_membership_id, pool, available, next_expires_at, next_expires_credits, expiring_within_60d, open_batches')
    .in('client_membership_id', ids);
  if (error) throw error;
  const map = {};
  (data || []).forEach(r => { map[r.client_membership_id] = r; });
  return map;
}

// De volledige batch-uitsplitsing. Bewust maar op twee plekken gebruikt
// (creditgeschiedenis cliënt + creditbeheerpaneel coach): het is diagnostiek,
// geen dagelijkse informatie.
export async function getMembershipCreditBatches(membershipIds) {
  const ids = (membershipIds || []).filter(Boolean);
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from('credit_batches')
    .select('id, client_membership_id, pool, source, granted_credits, remaining_credits, granted_at, expires_at, expired_at, note')
    .in('client_membership_id', ids)
    .lte('granted_at', new Date().toISOString())
    .order('expires_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

// De meest recente wekelijkse credittoekenning van één abonnement, uit de
// batchlijst die `getMembershipCreditBatches` al ophaalt. Dit is de
// vervanger van `client_memberships.last_credit_booking_at`: die kolom
// hoorde bij het maandmodel, wordt sinds het weekmodel nergens meer
// geschreven en stond op elk bestaand abonnement op NULL. `initial_week`
// telt bewust niet mee — dat is de eerste toekenning bij inschrijven, geen
// verlenging. Retourneert null als er nog niets is bijgeschreven.
export function laatsteWeeklyGrant(batches, membershipId) {
  if (!membershipId) return null;
  return (batches || [])
    .filter(b => b.client_membership_id === membershipId && b.source === 'weekly_grant')
    .reduce((nieuwste, b) => (
      !nieuwste || new Date(b.granted_at) > new Date(nieuwste.granted_at) ? b : nieuwste
    ), null);
}

// Hangt het saldo uit `overviewRows` aan elk membership als `credit_balance`.
// De pool van een abonnement is zijn `session_type`; alleen als er voor die
// pool geen rij is valt hij terug op de enige rij die er wél is (een
// abonnement heeft in de praktijk precies één pool, maar raden doen we niet).
export function mergeCreditBalances(memberships, overviewRows) {
  const rows = overviewRows || [];
  return (memberships || []).map(m => {
    const eigen = rows.filter(r => r.client_membership_id === m.id);
    const match = eigen.find(r => r.pool === m.session_type) || (eigen.length === 1 ? eigen[0] : null);
    return { ...m, credit_balance: match || null };
  });
}

// Haalt ALLE actieve (en opgezegde-maar-nog-lopende) memberships van een
// client op in één query — géén .limit(1)/.maybeSingle(), want een client
// kan meerdere gelijktijdige abonnementen hebben (bv. Small Group + PT
// 1-op-1). Zelfde querylogica als de mobiele app (services/database.js
// getActiveClientMemberships), gerepliceerd voor de web-portal's eigen
// datalaag (aparte tech-stack, geen gedeeld package).
//
// `last_credit_booking_at` is hier sinds 2026-08-09 uit: de opzegtermijn
// komt nu uit de SQL-helper `membership_cancellation_date()`. Niets leest
// die kolom nog, dus fase 4 kan hem droppen.
export async function getActiveClientMemberships(clientId) {
  const [{ data, error }, overview] = await Promise.all([
    supabase
      .from('client_memberships')
      .select(`
        id,
        client_id,
        session_type,
        credits_per_week,
        start_date,
        end_date,
        is_custom,
        is_rolling,
        cancellation_date,
        initial_end_date,
        custom_name,
        custom_price,
        custom_credits,
        custom_duration_days,
        membership_type:membership_types(id, name, legacy_credits_per_period, session_type, sessions_per_week_frequency)
      `)
      .eq('client_id', clientId)
      .eq('status', 'active')
      .order('start_date', { ascending: false }),
    getClientCreditOverview(clientId).catch(() => []),
  ]);
  if (error) throw error;
  return mergeCreditBalances(data || [], overview);
}

// Het bruikbare creditsaldo van een abonnement — het hoofdgetal in de kop.
export function primaryMembershipCredits(m) {
  return m?.credit_balance?.available ?? 0;
}

// De subregel onder de kop: "waarvan N vervallen op dd-mm-jjjj". Alleen tonen
// als er binnen 60 dagen daadwerkelijk iets vervalt — `expiring_within_60d`
// wordt serverside bepaald, zodat app, webportal en de meldingscron (fase 5)
// dezelfde grens hanteren. Retourneert null als er niets te melden valt.
export function creditExpiryNotice(m, t) {
  const b = m?.credit_balance;
  if (!b || !b.next_expires_at) return null;
  if (!(b.expiring_within_60d > 0) || !(b.next_expires_credits > 0)) return null;
  return t('credits.vervaltSubregel')
    .replace('{aantal}', b.next_expires_credits)
    .replace('{datum}', fmtVervalDatum(b.next_expires_at));
}

export function fmtVervalDatum(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('nl-NL', {
    timeZone: 'Europe/Amsterdam', day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// Voluit geschreven datum ("8 november 2026") — de cliëntgerichte notatie van
// de geldigheidsweergave, identiek aan de mobiele app. Accepteert een ISO-string
// of een Date.
export function fmtDatumLang(waarde) {
  if (!waarde) return '';
  const d = waarde instanceof Date ? waarde : new Date(waarde);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nl-NL', {
    timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'long', year: 'numeric',
  });
}

// De eerstvolgende wekelijkse bijschrijving. De week loopt vanaf `start_date`
// (verjaardagsweek, variant A) en niet vanaf maandag — exact dezelfde formule
// als `_credit_week_start` in de database:
//   weekstart = start_date + floor((dag - start_date)/7)*7
// De kolommen `next_credit_grant`/`last_credit_booking_at` zijn hiervoor NIET
// bruikbaar: die hoorden bij het maandmodel en worden sinds het weekmodel
// nooit meer gevuld. Retourneert een Date, of null als er niets meer bijkomt.
export function nextCreditGrantDate(m) {
  if (!m?.start_date) return null;
  const start = new Date(`${String(m.start_date).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime())) return null;
  const dagen = Math.floor((new Date() - start) / 86400000);
  const weken = dagen > 0 ? Math.floor(dagen / 7) : 0;
  const volgende = new Date(start.getTime() + (weken + 1) * 7 * 86400000);
  // Na de contracteinddatum of een eerdere opzegdatum komt er niets meer bij —
  // zelfde stopvoorwaarden als `nextCreditGrantDate` in de app (helpers.js).
  const stopDatums = [m.end_date, m.cancellation_date].filter(Boolean);
  for (const stopDatum of stopDatums) {
    const eind = new Date(`${String(stopDatum).slice(0, 10)}T23:59:59`);
    if (!Number.isNaN(eind.getTime()) && volgende > eind) return null;
  }
  return volgende;
}

export function membershipDisplayName(m, unknownLabel) {
  return m.is_custom && m.custom_name ? m.custom_name : (m.membership_type?.name || unknownLabel);
}
